"use client";

import { cn } from "../../../../utils";
import type { TableRowType } from "../../Table.types";
import { getDefaultGroupId } from "../../Table.utils";
import { TableGroupMobileProps } from "./Table.types.mobile";
import { TableRowMobile } from "./TableRow.mobile";

export const TableGroupMobile = <
  Row extends TableRowType,
  Group extends string = never,
>(
  props: TableGroupMobileProps<Row, Group>,
) => {
  const {
    row,
    group,
    columns,
    classes,
    cardIndex,
    activeGroupIds,
    getGroupId = getDefaultGroupId,
    onGroupClick,
    renderGroupEffect,
    ...rest
  } = props;

  const groupId = group.group
    ? getGroupId(row, group.group, cardIndex)
    : undefined;
  const isGroupActive =
    groupId && activeGroupIds ? activeGroupIds.includes(groupId) : false;
  const groupColumns = columns.slice(group.start, group.end + 1);
  const isGroupDisabled = groupColumns.every(
    (column) =>
      typeof column.isEnabled === "function" && !column.isEnabled(row),
  );

  const handleGroupClick = () => {
    if (group.group && groupId && onGroupClick) {
      onGroupClick({ row, group: group.group, groupId, rowIndex: cardIndex });
    }
  };

  if (isGroupDisabled) return null;

  return (
    <div
      className={cn(
        "ui:border-b ui:border-divider ui:last:border-b-0",
        {
          "ui:relative ui:cursor-pointer": group.group,
        },
        classes?.group,
      )}
      {...(group.group &&
        onGroupClick && {
          onClick: handleGroupClick,
          role: "button",
          tabIndex: 0,
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleGroupClick();
            }
          },
        })}
    >
      {group.group
        ? (renderGroupEffect?.({
            row,
            group: group.group,
            rowIndex: cardIndex,
            isActive: isGroupActive,
          }) ?? null)
        : null}

      {groupColumns.map((column, columnIndex) => {
        const rowIndex = group.start + columnIndex;

        return (
          <TableRowMobile<Row, Group>
            {...rest}
            row={row}
            column={column}
            classes={classes}
            rowIndex={rowIndex}
            cardIndex={cardIndex}
            key={column.id as string}
          />
        );
      })}
    </div>
  );
};
